import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import PageLayout from '../../components/Layout/PageLayout';
import { First3End3Model, First3End3QueryModel } from '../../types/first3end3';

const First3End3Page: React.FC = () => {
  const { t } = useTranslation();

  // State management
  const [data, setData] = useState<First3End3Model | null>(null);
  const [loading, setLoading] = useState(false);

  // Contract numbers for dropdown
  const [contractNumbers, setContractNumbers] = useState<string[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  // Search filters
  const [queryParams, setQueryParams] = useState<First3End3QueryModel>({
    contractNo: '',
    mixId: '',
    testDate: new Date().toISOString().split('T')[0]
  });

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Fetch contract numbers on component mount
  useEffect(() => {
    const fetchContractNumbers = async () => {
      try {
        setLoadingContracts(true);
        const numbers = await apiService.getContractNumbers();
        setContractNumbers(numbers);
      } catch (error) {
        console.error('Error fetching contract numbers:', error);
        // 即使失敗也繼續，允許用戶自由輸入
      } finally {
        setLoadingContracts(false);
      }
    };

    fetchContractNumbers();
  }, []);

  // Show snackbar notification
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch records from API
  const fetchRecords = async () => {
    // Validate required fields
    if (!queryParams.contractNo || !queryParams.mixId || !queryParams.testDate) {
      showSnackbar('請輸入合約號碼、MixID 和測試日期', 'error');
      return;
    }

    try {
      setLoading(true);
      setData(null);

      // Call the API method
      const response = await apiService.getStage2First3End3(
        queryParams.contractNo || '',
        queryParams.mixId || '',
        queryParams.testDate || ''
      );

      if (response) {
        setData(response);

        // Show error message if search failed
        if (response.searchSuccess === 1) {
          showSnackbar('查詢失敗，請檢查輸入的資料是否正確', 'error');
        }
      } else {
        setData(null);
        showSnackbar(t('pages.first3End3.messages.loadingError'), 'error');
      }
    } catch (error: any) {
      console.error('Error fetching First3End3 records:', error);
      setData(null);
      const errorMessage = error?.response?.data?.message || t('pages.first3End3.messages.loadingError');
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    fetchRecords();
  };

  // Handle reset filters
  const handleResetFilters = () => {
    const defaultParams: First3End3QueryModel = {
      contractNo: '',
      mixId: '',
      testDate: ''
    };
    setQueryParams(defaultParams);
    setData(null);
  };

  const renderDateCard = (label: string, date: string, index?: number) => {
    if (!date) return null;
    return (
      <Card
        variant="outlined"
        sx={{
          mb: 1,
          backgroundColor: index === 1 ? '#e3f2fd' : 'background.paper',
          border: index === 1 ? '2px solid #2196f3' : '1px solid rgba(0,0,0,0.12)'
        }}
      >
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: index === 1 ? 600 : 400 }}>
              {label}: {date}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderInfoSection = (title: string, content: string | React.ReactNode, emptyMessage: string = '無') => {
    return (
      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ minHeight: '24px' }}>
          {content || <span style={{ color: '#999' }}>{emptyMessage}</span>}
        </Typography>
      </Box>
    );
  };

  return (
    <PageLayout title={t('pages.first3End3.title')}>
      <Container maxWidth="xl">
        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('common.queryConditions')}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={contractNumbers}
                value={queryParams.contractNo}
                onInputChange={(event, newInputValue) => {
                  setQueryParams({ ...queryParams, contractNo: newInputValue });
                }}
                onChange={(event, newValue) => {
                  setQueryParams({ ...queryParams, contractNo: newValue || '' });
                }}
                loading={loadingContracts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    label={t('pages.first3End3.fields.contractNo')}
                    placeholder={t('pages.first3End3.fields.contractNo')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingContracts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={t('pages.first3End3.fields.mixId')}
                value={queryParams.mixId}
                onChange={(e) => setQueryParams({ ...queryParams, mixId: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loading) {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder={t('pages.first3End3.fields.mixId')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('pages.first3End3.fields.testDate')}
                value={queryParams.testDate}
                onChange={(e) => setQueryParams({ ...queryParams, testDate: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loading) {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? t('pages.first3End3.buttons.loading') : t('pages.first3End3.buttons.query')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleResetFilters}
                  disabled={loading}
                >
                  {t('pages.first3End3.buttons.reset')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Results */}
        {loading ? (
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          </Paper>
        ) : data && data.searchSuccess === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              查詢結果
            </Typography>

            <Grid container spacing={3}>
              {/* Left Column - CD Mark Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      CD Cube Mark 資訊
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {renderInfoSection(
                      '今次 CD Cube Mark',
                      data.nowCDMark || '',
                      '無資料'
                    )}

                    <Box sx={{ mt: 2 }}>
                      {renderInfoSection(
                        '前一次 CD Cube Mark',
                        data.preCDMark || '',
                        '無資料'
                      )}
                      {data.preCDMarkDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                          日期: {data.preCDMarkDate}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column - Query Parameters */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      查詢參數
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {renderInfoSection('合約號碼', data.setContractID)}
                    {renderInfoSection('MixID', data.setMixID)}
                    {renderInfoSection('選擇日期', data.setSelectedDate)}
                  </CardContent>
                </Card>
              </Grid>

              {/* First 3 Dates */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      前3日期
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {data.pre3Date1 ? renderDateCard('日期 1', data.pre3Date1, 1) : renderDateCard('日期 1','/', 1)}
                    {data.pre3Date2 ? renderDateCard('日期 2', data.pre3Date2, 2) : renderDateCard('日期 2','/', 2)}
                    {data.pre3Date3 ? renderDateCard('日期 3', data.pre3Date3, 3) : renderDateCard('日期 3','/', 3)}

                    {/* {!data.pre3Date1 && !data.pre3Date2 && !data.pre3Date3 && (
                      <Typography variant="body2" color="text.secondary">
                        無資料
                      </Typography>
                    )} */}
                  </CardContent>
                </Card>
              </Grid>

              {/* After 3 Dates */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      後3日期
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {data.after3Date1 ? renderDateCard('日期 1', data.after3Date1, 1) : renderDateCard('日期 1','/', 1)}
                    {data.after3Date2 ? renderDateCard('日期 2', data.after3Date2, 2) : renderDateCard('日期 2','/', 2)}
                    {data.after3Date3 ? renderDateCard('日期 3', data.after3Date3, 3) : renderDateCard('日期 3','/', 3)}

                    {/* {!data.after3Date1 && !data.after3Date2 && !data.after3Date3 && (
                      <Typography variant="body2" color="text.secondary">
                        無資料
                      </Typography>
                    )} */}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        ) : data && data.searchSuccess === 1 ? (
          <Paper sx={{ p: 3 }}>
            <Alert severity="warning">
              查詢失敗，請檢查輸入的資料是否正確。可能的原因：
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>合約號碼不存在</li>
                <li>MixID 不存在</li>
                <li>MixID 不存在於該合約號碼中</li>
                <li>選擇的日期不存在前3或後3資料</li>
              </ul>
            </Alert>
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Alert severity="info">{t('pages.first3End3.messages.noRecordsFound')}</Alert>
          </Paper>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          // anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </PageLayout>
  );
};

export default First3End3Page;